import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import ConnectServicesClientSelector from "../ConnectServicesClientSelector";

const EnableAutomatedSendModal = ({
  show = false,
  onClose = () => {},
  clients = [],
  clientRoles = [],
  listingUuid = "",
  setConnectedService = () => {},
  closeDate = null,
}) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const daysUntilClose = useMemo(() => {
    if (!closeDate) return null;
    const parsed = dayjs(closeDate, ["MM/DD/YYYY", "YYYY-MM-DD"]);
    if (!parsed.isValid()) return null;
    return parsed.diff(dayjs().startOf("day"), "days");
  }, [closeDate]);

  const hasError = submitted && !selectedClient;

  const clientOptions = (clients || []).map((client) => ({
    label: `${client.person.name} ${client.person.email_address}`,
    value: client.id,
    roleId: client.role?.id,
    name: client.person.name,
    email: client.person.email_address,
  }));

  const handleEnable = async () => {
    setSubmitted(true);
    if (!selectedClient) return;
    setIsLoading(true);
    const payload = {
      uuid: listingUuid,
      enabled: true,
      authenticity_token: window.ReactOnRails.authenticityToken(),
    };
    if (selectedClient.value) {
      payload.listing_person_id = selectedClient.value;
    } else if (selectedClient.role && selectedClient.client) {
      payload.role_id = selectedClient.role.value;
      payload.person_id = selectedClient.client.value;
    }
    const response = await caseTransformingAxios.patch("/api/v4/transactions/connected_services", payload);
    setConnectedService(response.data);
    setIsLoading(false);
    onClose();
  };

  const handleClose = () => {
    setSelectedClient(null);
    setSubmitted(false);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-min-h-full tw-max-h-full"
      contentClassName="tw-w-[602px] tw-m-auto"
    >
      <Modal.Header closeButton title="Connect Services Automation" />

      <Modal.Body className="tw-flex tw-flex-col tw-gap-[32px] tw-mt-[12px]">
        <ConnectServicesClientSelector
          clientOptions={clientOptions}
          selectedClient={selectedClient}
          onClientChange={setSelectedClient}
          error={hasError ? "Please select a client." : null}
          clientRoles={clientRoles}
          uuid={listingUuid}
          showSendTodayText={daysUntilClose !== null && daysUntilClose >= 0 && daysUntilClose <= 20}
          showSendFutureText={daysUntilClose >= 21}
        />
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-mt-[32px]">
        <Button size="medium" schema="tertiary" onClick={handleClose}>
          Cancel
        </Button>
        <Button size="medium" schema="primary" isLoading={isLoading} onClick={handleEnable}>
          Enable
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EnableAutomatedSendModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  clients: PropTypes.arrayOf(PropTypes.shape({})),
  clientRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  listingUuid: PropTypes.string,
  setConnectedService: PropTypes.func,
  closeDate: PropTypes.string,
};

export default EnableAutomatedSendModal;
